import { Routes } from '../api-routes'
import { SudoContextCaseWorkerDTO } from '../dtos/caseWorker'
import { FeatureFlagOrganizationDTO } from '../dtos/feature-flag'
import { RequestPayload, ResponseData, SuccessResponseDTO } from '../dtos/generic'
import { Language, PromptTemplateDTO, PromptTemplateType, SudoContextOrganizationDTO } from '../dtos/organization'
import { LogItem, TakeSnapshotBeneficiaryOptions, TakeSnapshotOrganizationOptions } from '../dtos/snapshot'
import { SudoContextUserDTO } from '../dtos/user'
import { getPath, setP } from './helpers'
import { defineRoute } from './new-helpers'

export const sudoControllerPath = '/sudo'

export const FeatureFlagRoutes = {
  getFeatureFlagsForOrganization: defineRoute<'GET', ResponseData<FeatureFlagOrganizationDTO[]>>({
    path: '/organization/:organizationId/feature-flags',
  }),

  toggleOrganization: defineRoute<'PATCH', ResponseData<SuccessResponseDTO>, RequestPayload<{ enabled: boolean }>>({
    path: '/sudo/feature-flags/:featureFlagKey/organization/:organizationId/toggle',
  }),
}

export namespace SudoRoutes {
  export namespace get {
    export function call() {
      return `/${Routes.Sudo}`
    }

    export type Response = {
      organizations: SudoContextOrganizationDTO[]
      users: SudoContextUserDTO[]
    }
  }

  export namespace organizations {
    export namespace createOne {
      export function call() {
        return `/${Routes.Sudo}/${Routes.Organizations}`
      }

      export type Request = { language: Language; name: string; url?: string }
      export type Response = { organizations: SudoContextOrganizationDTO[] }
    }

    export namespace patchOne {
      export function call(p = setP(['organizationId'])) {
        return getPath(p, { prefix: Routes.Sudo })
      }

      export type Request = {
        fields: Partial<Pick<SudoContextOrganizationDTO, 'name' | 'lifeCycleStage'>>
      }
      export type Response = { organizations: SudoContextOrganizationDTO[] }
    }
    export namespace hardDeleteOne {
      export function call(p = setP(['organizationId'])) {
        return getPath(p, { prefix: Routes.Sudo, suffix: 'hardDelete' })
      }
      export type Response = { success: boolean; errorMessage: string }
    }
  }

  export namespace users {
    export namespace createOne {
      export function call() {
        return `/${Routes.Sudo}/${Routes.Organizations}/${Routes.Users}`
      }

      export type Request = {
        fields: Omit<SudoContextUserDTO, 'id'> & { password: string }
      }
      export type Response = { users: SudoContextUserDTO[] }
    }

    export namespace disableOne {
      export function call(p = setP(['userId'])) {
        return getPath(p, { suffix: 'disable', prefix: Routes.Sudo })
      }

      export type Response = { user: SudoContextUserDTO }
    }

    export namespace enableOne {
      export function call(p = setP(['userId'])) {
        return getPath(p, { suffix: 'enable', prefix: Routes.Sudo })
      }

      export type Response = { user: SudoContextUserDTO }
    }

    export namespace patchOne {
      export function call(p = setP(['userId'])) {
        return getPath(p, { prefix: Routes.Sudo })
      }

      export type Request = {
        fields: Partial<Pick<SudoContextUserDTO, 'firstName' | 'lastName' | 'email' | 'superAdmin' | 'organizationIds'>>
      }
      export type Response = { user: SudoContextUserDTO }
    }

    export namespace patchOnePassword {
      export function call(p = setP(['userId'])) {
        return getPath(p, { suffix: 'password', prefix: Routes.Sudo })
      }

      export type Request = { password: string }
      export type Response = boolean
    }

    export namespace getCaseWorkersFromOne {
      export function call(p = setP(['userId'])) {
        return getPath(p, { suffix: Routes.CaseWorkers, prefix: Routes.Sudo })
      }

      export type Response = { caseWorkers: SudoContextCaseWorkerDTO[] }
    }
  }

  export namespace prompts {
    export namespace get {
      export function call() {
        return `/${Routes.Sudo}/${Routes.PromptTemplates}`
      }
      export type Request = { promptTemplateType: PromptTemplateType }
      export type Response = {
        defaultPrompt: string
        codePrompt: string
        organizationPrompts: {
          organizationId: string
          prompt: PromptTemplateDTO
        }[]
      }
    }
  }

  export namespace snapshot {
    export namespace takeSnapshot {
      export function call() {
        return `/${Routes.Sudo}/${Routes.Snapshot}/take`
      }

      export type Request = {
        organizationId: string
        organizationOptions: TakeSnapshotOrganizationOptions
        beneficiaryOptions: TakeSnapshotBeneficiaryOptions
        onlyBeneficiaryIds?: string[]
      }
      export type Response = { snapshotId: string; snapshotData: string }
    }
    export namespace loadSnapshot {
      export function call() {
        return `/${Routes.Sudo}/${Routes.Snapshot}/load`
      }

      export type Request = {
        file: File
        name: string
        language: string
        url?: string
      }
      export type Response = {
        processedOrganization: SudoContextOrganizationDTO
        logs: LogItem[]
        errorsOnLoad: string[]
      }
    }
  }
}
